import { el, clear, showToast, showConfirm, showPrompt, statusLabel, unitLabel, t } from './dom';
import { api, ApiError } from './api';
import type { Order, Product, Category, User } from './types';
import { COMPANY } from './config/company';
import { i18n } from './i18n/i18n';

export async function renderOrderEditor(
  root: HTMLElement,
  orderId: string,
  user: User,
  onBack: () => void,
) {
  clear(root);

  const header = el('header', { className: 'header' });

  const brand = el('div', { className: 'header-brand' });
  const logoImg = document.createElement('img');
  logoImg.src = COMPANY.logoUrl;
  logoImg.alt = COMPANY.name;
  logoImg.className = 'header-logo';
  brand.appendChild(logoImg);
  brand.appendChild(el('h1', { className: 'header-title', text: t('orderEditor.title') }));

  header.append(
    el('button', {
      className: 'btn-logout',
      text: t('common.back'),
      onClick: onBack,
    }),
    brand,
    el('span', {}),
  );

  const main = el('main', { className: 'main' });
  const content = el('div');
  main.appendChild(content);
  root.append(header, main);

  let order: Order | null = null;
  let categories: Category[] = [];
  let products: Product[] = [];

  const loadAll = async () => {
    clear(content);
    content.appendChild(el('p', { text: t('common.loading'), className: 'loading' }));

    try {
      const [orderResult, productResult] = await Promise.all([
        api.request<Order>('GET', `/orders/${orderId}`),
        api.request<{ categories: Category[]; products: Product[] }>('GET', '/orders/products'),
      ]);
      order = orderResult;
      categories = productResult.categories;
      products = productResult.products;
      render();
    } catch (e) {
      clear(content);
      const msg = e instanceof ApiError ? e.message : t('common.networkError');
      content.appendChild(el('div', { className: 'empty-state', text: msg }));
    }
  };

  const render = () => {
    if (!order) return;
    clear(content);

    const isOwner = order.createdByUserId === user.id;
    const isAdmin = user.role === 'ADMIN';
    const canEdit = (user.role === 'EMPLOYEE' && isOwner && order.status === 'DRAFT') ||
                    (isAdmin && order.status === 'UNDER_REVIEW' && order.lockedByAdminId === user.id);

    // Letterhead - solo visible al imprimir
    const letterhead = buildPrintLetterhead(order);
    content.appendChild(letterhead);

    // Header del pedido
    const card = el('div', { className: 'card' });
    const cardHeader = el('div', { className: 'card-header' });
    cardHeader.append(
      el('div', {}, [
        el('div', { className: 'card-title', text: order.orderNumber }),
        el('div', { className: 'order-meta', text: t('orders.weekOf', { week: order.weekNumber, year: order.year }) }),
      ]),
      el('span', { className: `badge badge-${order.status}`, text: statusLabel(order.status) }),
    );
    card.appendChild(cardHeader);
    content.appendChild(card);

    // Form de agregar producto
    if (canEdit) {
      const addCard = el('div', { className: 'card' });
      addCard.appendChild(el('div', { className: 'card-title', text: t('orderEditor.addProduct') }));

      const productGroup = el('div', { className: 'form-group' });
      productGroup.appendChild(el('label', { text: t('orderEditor.product') }));
      const select = document.createElement('select');
      select.appendChild(el('option', { text: t('orderEditor.chooseProduct'), attrs: { value: '' } }));

      // Agrupar por categoría
      for (const cat of categories) {
        const catProducts = products.filter(p => p.categoryId === cat.id);
        if (catProducts.length === 0) continue;
        const optgroup = document.createElement('optgroup');
        optgroup.label = cat.name;
        for (const p of catProducts) {
          // Filtrar productos ya agregados
          if (order!.items.some(i => i.productId === p.id)) continue;
          const opt = document.createElement('option');
          opt.value = p.id;
          opt.textContent = `${p.name} (${unitLabel(p.unit)})`;
          optgroup.appendChild(opt);
        }
        if (optgroup.children.length > 0) {
          select.appendChild(optgroup);
        }
      }
      productGroup.appendChild(select);

      const qtyGroup = el('div', { className: 'form-group' });
      qtyGroup.appendChild(el('label', { text: t('orderEditor.quantity') }));
      const qtyInput = document.createElement('input');
      qtyInput.type = 'number';
      qtyInput.step = '0.01';
      qtyInput.min = '0.01';
      qtyInput.placeholder = '0';
      qtyGroup.appendChild(qtyInput);

      const obsGroup = el('div', { className: 'form-group' });
      obsGroup.appendChild(el('label', { text: t('orderEditor.observationsOptional') }));
      const obsInput = document.createElement('input');
      obsInput.type = 'text';
      obsInput.maxLength = 500;
      obsGroup.appendChild(obsInput);

      const addBtn = el('button', {
        className: 'btn btn-primary',
        text: t('orderEditor.addToOrder'),
        onClick: async () => {
          const productId = select.value;
          const qty = parseFloat(qtyInput.value);
          if (!productId || !Number.isFinite(qty) || qty <= 0) {
            showToast(t('orderEditor.invalidProduct'), 'error');
            return;
          }
          addBtn.disabled = true;
          try {
            await api.request<Order>('POST', `/orders/${orderId}/items`, {
              productId,
              quantity: qty,
              observations: obsInput.value.trim() || null,
            });
            showToast(t('orderEditor.added'), 'success');
            await loadAll();
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : t('common.unknownError');
            showToast(msg, 'error');
            addBtn.disabled = false;
          }
        },
      });

      addCard.append(productGroup, qtyGroup, obsGroup, addBtn);
      content.appendChild(addCard);
    }

    // Tabla de ítems
    const itemsCard = el('div', { className: 'card' });
    itemsCard.appendChild(el('div', { className: 'card-title', text: t('orderEditor.products', { n: order.items.length }) }));

    if (order.items.length === 0) {
      itemsCard.appendChild(el('div', { className: 'empty-state', text: t('orderEditor.empty') }));
    } else {
      const table = el('table', { className: 'items-table' });
      const thead = el('thead');
      const headRow = el('tr');

      // Columnas dinamicas: el empleado no ve "Subtotal" (precio)
      const headerLabels: string[] = [
        t('orderEditor.sku'),
        t('orderEditor.product'),
        t('orderEditor.quantity'),
        t('orderEditor.unit'),
      ];
      if (isAdmin) headerLabels.push(t('orderEditor.subtotal'));
      headerLabels.push(t('orderEditor.observations'));
      headerLabels.push(''); // Columna de acciones

      headerLabels.forEach(text => {
        headRow.appendChild(el('th', { text }));
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = el('tbody');
      let totalCents = 0;

      for (const item of order.items) {
        const tr = el('tr');
        const qty = Number(item.quantity);
        const unitPriceCents = item.product?.referencePrice ?? 0;
        const subtotalCents = Math.round(qty * unitPriceCents);
        totalCents += subtotalCents; // Se calcula igual; solo no se muestra al empleado

        tr.append(
          el('td', { text: item.product?.sku ?? '—' }),
          el('td', { text: item.product?.name ?? 'N/A' }),
          el('td', { text: String(item.quantity), className: 'numeric' }),
          el('td', { text: unitLabel(item.unit) }),
        );

        if (isAdmin) {
          const subtotalText = unitPriceCents > 0
            ? `€ ${(subtotalCents / 100).toFixed(2).replace('.', ',')}`
            : '—';
          tr.appendChild(el('td', { text: subtotalText, className: 'numeric' }));
        }

        tr.appendChild(el('td', { text: item.observations ?? '' }));

        const actionsCell = el('td', { className: 'no-print' });
        if (canEdit) {
          actionsCell.appendChild(el('button', {
            className: 'btn btn-secondary',
            text: t('orderEditor.remove'),
            onClick: async () => {
              if (!await showConfirm(t('orderEditor.removeConfirm'))) return;
              try {
                await api.request<Order>('DELETE', `/orders/${orderId}/items/${item.id}`);
                showToast(t('orderEditor.removed'), 'success');
                await loadAll();
              } catch (e) {
                const msg = e instanceof ApiError ? e.message : t('common.unknownError');
                showToast(msg, 'error');
              }
            },
          }));
        }
        tr.appendChild(actionsCell);
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);

      // Fila de total - solo admin
      if (isAdmin && totalCents > 0) {
        const tfoot = el('tfoot');
        const totalRow = el('tr', { className: 'total-row' });
        totalRow.append(
          el('td', { attrs: { colspan: '4' } }),
          el('td', {
            text: t('orderEditor.totalEstimated'),
            className: 'total-label',
          }),
          el('td', {
            text: `€ ${(totalCents / 100).toFixed(2).replace('.', ',')}`,
            className: 'numeric total-value',
          }),
          el('td', { className: 'no-print' }),
        );
        tfoot.appendChild(totalRow);
        table.appendChild(tfoot);
      }

      itemsCard.appendChild(table);

      // Nota del total - solo admin
      if (isAdmin && totalCents > 0) {
        const note = el('p', {
          className: 'total-note',
          text: t('orderEditor.totalNote'),
        });
        itemsCard.appendChild(note);
      }
    }
    content.appendChild(itemsCard);

    // Acciones del pedido
    const actionsCard = el('div', { className: 'card' });
    const toolbar = el('div', { className: 'toolbar' });

    if (user.role === 'EMPLOYEE' && order.status === 'DRAFT' && order.items.length > 0) {
      toolbar.appendChild(el('button', {
        className: 'btn btn-success',
        text: t('orderEditor.submit'),
        onClick: async () => {
          if (!await showConfirm(t('orderEditor.submitConfirm'))) return;
          try {
            await api.request<Order>('POST', `/orders/${orderId}/submit`);
            showToast(t('orderEditor.submitted'), 'success');
            onBack();
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : t('common.unknownError');
            showToast(msg, 'error');
          }
        },
      }));
    }

    if (user.role === 'ADMIN') {
      if (order.status === 'SUBMITTED') {
        toolbar.appendChild(el('button', {
          className: 'btn btn-primary',
          text: t('admin.takeForReview'),
          onClick: async () => {
            try {
              await api.request<Order>('POST', `/admin/orders/${orderId}/take-for-review`);
              showToast(t('admin.taken'), 'success');
              await loadAll();
            } catch (e) {
              const msg = e instanceof ApiError ? e.message : t('common.unknownError');
              showToast(msg, 'error');
            }
          },
        }));
      }

      if (order.status === 'UNDER_REVIEW' && order.lockedByAdminId === user.id) {
        toolbar.append(
          el('button', {
            className: 'btn btn-success',
            text: t('admin.approve'),
            onClick: async () => {
              if (!await showConfirm(t('admin.approveConfirm'))) return;
              try {
                await api.request<Order>('POST', `/admin/orders/${orderId}/approve`);
                showToast(t('admin.approved'), 'success');
                await loadAll();
              } catch (e) {
                const msg = e instanceof ApiError ? e.message : t('common.unknownError');
                showToast(msg, 'error');
              }
            },
          }),
          el('button', {
            className: 'btn btn-danger',
            text: t('admin.reject'),
            onClick: async () => {
              const reason = await showPrompt(t('admin.rejectReason'), {
                multiline: true,
                required: true,
                minLength: 10,
              });
              if (!reason) return;
              try {
                await api.request<Order>('POST', `/admin/orders/${orderId}/reject`, { reason });
                showToast(t('admin.rejected'), 'success');
                await loadAll();
              } catch (e) {
                const msg = e instanceof ApiError ? e.message : t('common.unknownError');
                showToast(msg, 'error');
              }
            },
          }),
        );
      }

      if (order.status === 'READY_TO_SEND') {
        toolbar.appendChild(el('button', {
          className: 'btn btn-success',
          text: t('admin.markAsSent'),
          onClick: async () => {
            const ref = await showPrompt(t('admin.metroRefPrompt'), {});
            try {
              await api.request<Order>('POST', `/admin/orders/${orderId}/mark-as-sent`, {
                metroReferenceNumber: ref || null,
              });
              showToast(t('admin.markedSent'), 'success');
              await loadAll();
            } catch (e) {
              const msg = e instanceof ApiError ? e.message : t('common.unknownError');
              showToast(msg, 'error');
            }
          },
        }));
      }
    }

    toolbar.appendChild(el('button', {
      className: 'btn btn-secondary no-print',
      text: t('orderEditor.print'),
      onClick: () => window.print(),
    }));

    if (toolbar.children.length > 0) {
      actionsCard.appendChild(toolbar);
      content.appendChild(actionsCard);
    }

    // Footer solo visible al imprimir
    content.appendChild(buildPrintFooter());
  };

  await loadAll();
}

// =====================================================
// Print helpers - letterhead y footer del PDF
// =====================================================

function buildPrintLetterhead(order: Order): HTMLElement {
  const letterhead = document.createElement('div');
  letterhead.className = 'print-letterhead';

  const row = document.createElement('div');
  row.className = 'print-letterhead-row';

  // Logo a la izquierda
  const logo = document.createElement('img');
  logo.src = COMPANY.logoUrl;
  logo.alt = COMPANY.name;
  logo.className = 'print-logo';
  row.appendChild(logo);

  // Datos a la derecha
  const info = document.createElement('div');
  info.className = 'print-company-info';

  const name = document.createElement('div');
  name.className = 'print-company-name';
  name.textContent = COMPANY.name;
  info.appendChild(name);

  const lines: string[] = [];
  if (COMPANY.legalName) lines.push(COMPANY.legalName);
  lines.push(COMPANY.street);
  lines.push(`${COMPANY.postalCode} ${COMPANY.city}`);
  if (COMPANY.country) lines.push(COMPANY.country);
  if (COMPANY.phone) lines.push(`${t('pdf.tel')}: ${COMPANY.phone}`);
  if (COMPANY.email) lines.push(COMPANY.email);
  if (COMPANY.website) lines.push(COMPANY.website);
  if (COMPANY.taxNumber) lines.push(`${t('pdf.taxNumber')}: ${COMPANY.taxNumber}`);
  if (COMPANY.vatId) lines.push(`${t('pdf.vatId')}: ${COMPANY.vatId}`);

  for (const line of lines) {
    const div = document.createElement('div');
    div.textContent = line;
    info.appendChild(div);
  }
  row.appendChild(info);

  letterhead.appendChild(row);

  // Título del documento
  const title = document.createElement('div');
  title.className = 'print-doc-title';
  const dateStr = i18n.formatDate(new Date());
  title.textContent = `${t('orderEditor.title')} ${order.orderNumber} — ${t('orders.weekOf', { week: order.weekNumber, year: order.year })}`;
  letterhead.appendChild(title);

  const dateLine = document.createElement('div');
  dateLine.style.fontSize = '0.8rem';
  dateLine.style.color = '#5C4A3F';
  dateLine.style.marginTop = '0.25rem';
  dateLine.textContent = dateStr;
  letterhead.appendChild(dateLine);

  return letterhead;
}

function buildPrintFooter(): HTMLElement {
  const footer = document.createElement('div');
  footer.className = 'print-footer';
  footer.textContent = `${COMPANY.name} · ${COMPANY.street}, ${COMPANY.postalCode} ${COMPANY.city}`;
  return footer;
}
